/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/features/attendanceModern/useAttendanceModernClock.ts
 * PURPOSE: Modern attendance clock-in/out với GPS validation và photo capture.
 *          2026-08-12: sync mobile PR #603 (BS-300 §1bis) — late/early clock-in
 *          timing warnings (G2/G5, non-blocking) + photo watermark (name/GPS/
 *          address/time), reading the tenant-configured early-tolerance the
 *          backend already returns from this same branches/locations call.
 *          2026-08-15: web-sync checklist A.5 — the branches/locations fetch
 *          had a silent empty catch; factored it into fetchBranchConfig(),
 *          added `branchConfigError` state, and exposed `retryBranchConfig`
 *          so AttendanceModern.tsx can show a retry banner.
 *          2026-08-17: BS-301 Route 2 §1 gaps closed —
 *          (1) "no shift today" state: exposes `todayScheduleChecked` +
 *          `hasShiftToday` so the page can hide the clock-in card and show
 *          the empty-state copy per the Gherkin "Hôm nay không có ca"
 *          scenario (previously the page always showed the clock-in card
 *          regardless of whether the employee had a shift today);
 *          (2) GPS-lệch (out-of-geofence) banner: clockIn now reads
 *          `validation.is_within_geofence`/`distance_meters` off the backend
 *          ClockInResponse (backend/app/api/v1/gps_attendance.py:226-227) and
 *          exposes `gpsDeviationWarning` — BS-300 §1bis-G1 already CHỐT
 *          "không chặn, chỉ đánh dấu GPS lệch" on the backend/mobile side,
 *          but the web page previously never surfaced the flag at all;
 *          (3) clockOut now queues offline on failure exactly like clockIn
 *          (was previously KHÔNG queue — see registry SC-020 G3/Responsive
 *          AC "nút vẫn hoạt động được... bản ghi được ghi vào hàng chờ đồng
 *          bộ cục bộ" for BOTH "Vào ca" and "Ra ca").
 *          2026-09-04: SC-017-EMP-ATTENDANCE [1] home IN-SHIFT gap — exposes
 *          `lastClockIn` (already returned by GET /attendance/status as
 *          `last_clock_in`, just never read before) + derived `elapsedMinutes`
 *          (mirrors mobile useAttendance.ts:155-158's
 *          `Math.round((Date.now() - new Date(last_clock_in).getTime())/60000)`)
 *          so AttendanceModern.tsx can render "{N} phút/giờ đã trôi qua" per
 *          the wireframe IN-SHIFT card — ticks every 60s while clocked in so
 *          the display doesn't go stale on a long-open tab.
 *          2026-09-04 (mobile-source-of-truth audit, known bug #1): fixed
 *          `fetchBranchConfig()` + `stampAndStorePhoto()` reading the tenant
 *          key from a localStorage key ('tenant_key') that nothing in this
 *          codebase ever writes — the real value lives in the persisted
 *          Zustand `auth-storage` blob (`state.tenantKey`, see src/store/auth.ts),
 *          same as apiWrapper.ts/config.ts/leaveRequest.ts already read it
 *          correctly. `branchConfig` was permanently null on any tenant that
 *          enforces `X-Tenant-Key`, so the [1] home GPS status line + geofence
 *          badge/watermark address could never render. Now reads `tenantKey`
 *          off `useAuthStore()` like everything else in this file already does.
 *          2026-10-04 (#1317, #1225 rv8 M1): clockIn queued EVERY failed
 *          response offline and told the user "saved offline", so a server
 *          refusal (409 ambiguous branch, 422 shift limit, 403, 5xx) never
 *          reached them. Now only a no-response network failure queues; any
 *          HTTP status shows the server message.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-04
 * COMPLIANCE: SDLC 6.5.0 + BS-300-PHAN-DOAN-C-NHAN-VIEN-CHAM-CONG §1bis (mobile↔web parity)
 *   + BS-301-PHAN-DOAN-C-NHAN-VIEN-WEB-REDESIGN §Route 2
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * @business-context
 * - Domain: Feature Modules, Vietnamese F&B Business Features
 * - Impact: Feature Delivery, Code Organization, Modularity
 * - Stakeholders: Product Team, Development Team, End Users
 *
 * @technical-architecture
 * - Layer: Feature Layer, Business Modules
 * - Pattern: Feature-Based Architecture, Module Encapsulation
 * - Dependencies: React 18, TypeScript, Feature-Specific Libraries
 *
 * @cross-references
 * - Design: docs/02-design/05-business-logic/
 * - Mobile source of truth: mobile/src/screens/attendance/AttendanceScreen.tsx (PR #603, commit 84d2a5abf)
 * - Timing logic: frontend/src/utils/attendanceTiming.ts
 * - Watermark logic: frontend/src/utils/attendancePhotoWatermark.ts
 *  */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { locationService } from '@/services/location'
import apiWrapper from '@/services/api/apiWrapper'
import { offlineManager } from '@/services/offlineManager'
import { scheduleApi } from '@/services/api/schedule'
import { managerProfileApi } from '@/services/api/managerProfileApi'
import { useAuthStore } from '@/store/auth'
import { computeClockInTimingWarning, computeClockOutTimingWarning, DEFAULT_EARLY_CLOCKIN_TOLERANCE_MINUTES, type ClockInTimingWarning, type ClockOutTimingWarning } from '@/utils/attendanceTiming'
import { stampAttendancePhoto } from '@/utils/attendancePhotoWatermark'
import { toLocalISODate } from '@/utils/date'

type Status = 'idle' | 'locating' | 'ready' | 'submitting' | 'success' | 'error'

/**
 * Branch GPS config fetched from backend.
 * Per gps-attendance-api.yaml §BranchLocation: max_clock_in_distance_meters is per-branch configurable (default 100m).
 */
interface BranchGPSConfig {
  branch_id: number;
  branch_name: string;
  geofence_radius_meters: number;
  max_clock_in_distance_meters: number;
  coordinates: { latitude: number; longitude: number };
}

export function useAttendanceModernClock() {
  const { user, accessToken, tenantKey } = useAuthStore()
  const [status, setStatus] = useState<Status>('idle')
  // Backend attendance status: 'clocked_in' | 'clocked_out' | null (loading)
  const [serverStatus, setServerStatus] = useState<string | null>(null)
  // #780: who closes a stale shift when GPS fails — tenant setting, default 'manager'.
  const [staleCloseMode, setStaleCloseMode] = useState<string>('manager')
  // SC-017-EMP-ATTENDANCE [8] checklist (2026-09-02) — bản ghi Attendance đang
  // mở (nếu có), cần cho các endpoint checklist activate/tick/status. Lấy từ
  // response clockIn() (vừa chấm công) HOẶC field attendance_id mới thêm vào
  // GET /attendance/status (đã chấm công từ trước, reload trang).
  const [currentAttendanceId, setCurrentAttendanceId] = useState<string | null>(null)
  // SC-017-EMP-ATTENDANCE [1] home IN-SHIFT (2026-09-04) — "{N} phút/giờ đã
  // trôi qua". Backend already returns `last_clock_in` from GET
  // /attendance/status (backend/app/api/v1/attendance.py:981); mirrors
  // currentAttendanceId's sibling field on the same response.
  const [lastClockIn, setLastClockIn] = useState<string | null>(null)
  // Per gps-attendance-api.yaml §BranchLocation: per-branch GPS config from backend
  const [branchConfig, setBranchConfig] = useState<BranchGPSConfig | null>(null)
  // BS-300 §1bis-G5 (PR #603 sync, 2026-08-12): TA cấu hình theo tenant, đọc từ
  // CÙNG response branches/locations mobile dùng — KHÔNG hardcode. Mặc định 30
  // phút cho tới khi API trả về (tương thích ngược, giống mobile useBranchLocations).
  const [earlyClockinToleranceMinutes, setEarlyClockinToleranceMinutes] = useState<number>(DEFAULT_EARLY_CLOCKIN_TOLERANCE_MINUTES)
  // BS-300 §1bis-G2 (PR #603 sync, 2026-08-12): giờ vào ca hôm nay, để tính cảnh
  // báo trễ/sớm — trước bản sửa này trang KHÔNG fetch lịch làm việc, không có
  // cách nào biết "trễ bao nhiêu phút".
  const [todayShiftStartTime, setTodayShiftStartTime] = useState<string | null>(null)
  // SC-017-EMP-ATTENDANCE [9] end_verify (2026-09-02) — giờ KẾT THÚC ca, cần
  // cho computeClockOutTimingWarning (trễ giờ RA, khác trễ giờ VÀO ở trên).
  const [todayShiftEndTime, setTodayShiftEndTime] = useState<string | null>(null)
  // BS-301 Route 2 §1 (2026-08-17): "Hôm nay không có ca" empty-state — the
  // schedule fetch above only ever set/left null todayShiftStartTime, which
  // is indistinguishable between "still loading" and "confirmed no shift
  // today". `todayScheduleChecked` flips true once the fetch settles
  // (success OR failure) so the page can safely render the empty-state only
  // after the check actually ran, not on every render before data arrives.
  const [todayScheduleChecked, setTodayScheduleChecked] = useState(false)
  const [hasShiftToday, setHasShiftToday] = useState(true)
  // web-sync checklist A.5 (2026-08-15): the branch/location fetch below used
  // to fail silently (empty catch — "Branch config unavailable"), leaving the
  // employee with no signal and no way to retry. Surface it + expose a retry.
  const [branchConfigError, setBranchConfigError] = useState<string | null>(null)
  // SC-017-EMP-ATTENDANCE [2] method_select (2026-09-01) — which clock-in
  // methods the tenant/branch currently has enabled (SC-004-ATTENDANCE-CONFIG),
  // so the method-select screen is config-driven instead of hardcoding all 3
  // methods as always visible. Separate from `fetchBranchConfig` above:
  // GET /api/v2/attendance/clock-config only returns enabled-method flags, not
  // branch coordinates/geofence radius (still sourced from the v1
  // branches/locations call). Default all 3 to `true` while loading/on error —
  // same fallback the backend itself uses for `timemark_method_enabled`
  // (escape-hatch method, must not disappear just because this one fetch failed).
  const [methodConfig, setMethodConfig] = useState<{ selfie: boolean; gps: boolean; timemark: boolean }>({
    selfie: true,
    gps: true,
    timemark: true,
  })

  // Per BR-ATTN-001 F2 + gps-attendance-api.yaml: fetch per-branch GPS config.
  // Factored out of the mount effect (was inline) so the A.5 retry button can
  // call the exact same fetch instead of duplicating it.
  //
  // 2026-09-04 (mobile-source-of-truth audit, known bug #1): this used to read
  // `localStorage.getItem('tenant_key')` — a key NOTHING in this codebase ever
  // writes (verified: `grep -rn "'tenant_key'" src/` outside this file only
  // matches other, unrelated dead reads). The real tenant key lives inside the
  // persisted Zustand `auth-storage` blob as `state.tenantKey` (see
  // src/store/auth.ts, and the same read pattern already used correctly by
  // apiWrapper.ts::getTenantKey / config.ts / leaveRequest.ts). Reading the
  // wrong key meant `X-Tenant-Key` was always sent empty on this fetch, and
  // `branchConfig` — plus every UI that depends on it (the [1] home GPS status
  // line added in d42d97d35, geofenceResult, watermark address) — could never
  // populate on a tenant that enforces the header. Fixed by reading `tenantKey`
  // off the already-imported `useAuthStore()` above instead of localStorage.
  const fetchBranchConfig = useCallback(async () => {
    setBranchConfigError(null)
    const configuredBase = import.meta.env.VITE_API_BASE_URL ?? '';
    const apiBase = configuredBase === '' ? `${window.location.protocol}//${window.location.host}` : configuredBase;
    try {
      const r = await fetch(`${apiBase}/api/v1/gps-attendance/branches/locations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`,
          'X-Tenant-Key': tenantKey || '',
        },
      })
      if (!r.ok) {
        setBranchConfigError('Không tải được thông tin chi nhánh/vị trí.')
        return
      }
      const data = await r.json()
      if (data?.branches?.length > 0) {
        setBranchConfig(data.branches[0])
      }
      // Note: an ok:true response with an empty `branches` array (tenant has
      // no branches configured, or the employee isn't assigned one yet) is a
      // valid response, NOT a fetch failure — only !r.ok / network / parse
      // errors below set branchConfigError.
      // BS-300 §1bis-G5: backend đã trả field này từ cùng endpoint (xem
      // gps_attendance.py get_branch_locations) — trước bản sửa này bị bỏ qua.
      if (typeof data?.early_clockin_tolerance_minutes === 'number') {
        setEarlyClockinToleranceMinutes(data.early_clockin_tolerance_minutes)
      }
    } catch {
      // Branch config unavailable (network/parse error) — timing-warning +
      // geofence-radius fall back to defaults (see validateLocation), but the
      // employee should be told rather than silently getting fallback values.
      setBranchConfigError('Không tải được thông tin chi nhánh/vị trí. Vui lòng kiểm tra kết nối mạng.')
    }
  }, [tenantKey])

  // Fetch current attendance status + branch GPS config from backend on mount
  useEffect(() => {
    apiWrapper.getAttendanceStatus().then((res) => {
      if (res.success && res.data) {
        setServerStatus((res.data as any).status || null)
        setCurrentAttendanceId((res.data as any).attendance_id || null)
        setLastClockIn((res.data as any).last_clock_in || null)
        setStaleCloseMode((res.data as any).stale_shift_close_mode || 'manager')
      } else {
        setServerStatus('not_clocked_in')
      }
    }).catch(() => setServerStatus('not_clocked_in'))

    fetchBranchConfig()

    // SC-017-EMP-ATTENDANCE [2] — independent of fetchBranchConfig (separate
    // endpoint, separate failure mode); a failure here must not block clock-in
    // status/branch-location from loading, and vice versa — same "1 widget lỗi
    // không sập cả trang" principle already applied to OT summary elsewhere.
    managerProfileApi.getClockConfig()
      .then((cfg) => {
        setMethodConfig({
          selfie: cfg.selfie_enabled,
          gps: cfg.gps_enabled,
          timemark: cfg.timemark_method_enabled,
        })
      })
      .catch(() => {
        // Leave methodConfig at its all-true default (declared above) — a
        // config-fetch failure must not hide every clock-in method.
      })
  }, [fetchBranchConfig])

  // BS-300 §1bis-G2: fetch lịch làm hôm nay (giờ vào ca) — mới, để có dữ liệu
  // tính cảnh báo trễ. Cùng pattern useEmployeeDashboard.ts Query 3.
  useEffect(() => {
    if (!accessToken) return
    const today = toLocalISODate(new Date())
    // scheduleApi.getMySchedule resolves directly to Schedule[] (apiRequest<T>
    // unwraps to T, no {success,data} envelope — same contract useEmployeeDashboard.ts relies on).
    scheduleApi.getMySchedule(accessToken, today, today)
      .then((list) => {
        const shift = Array.isArray(list) ? list[0] : null
        setTodayShiftStartTime(shift?.start_time ?? null)
        setTodayShiftEndTime(shift?.end_time ?? null)
        // BS-301 Route 2 §1 (2026-08-17): a confirmed empty list means the
        // employee genuinely has no shift today (not a fetch failure — see
        // catch below, which leaves hasShiftToday at its safe default so a
        // network error never masquerades as "no shift").
        setHasShiftToday(Array.isArray(list) && list.length > 0)
        setTodayScheduleChecked(true)
      })
      .catch(() => {
        // Fetch failed (network/server) — NOT the same as "confirmed no
        // shift". Leave hasShiftToday at its safe default (true) so the
        // clock-in button doesn't get hidden by a transient fetch error;
        // todayScheduleChecked stays false too, so the page keeps rendering
        // its normal (loading-tolerant) clock-in card instead of the
        // empty-state copy.
      })
  }, [accessToken])

  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [photoBase64, setPhotoBase64] = useState<string | null>(null)
  const [location, setLocation] = useState<{ lat: number; lon: number; accuracy?: number } | null>(null)
  const [accuracyInfo, setAccuracyInfo] = useState<{ isValid: boolean; accuracy: number; threshold: number; message?: string } | null>(null)
  const [overridden, setOverridden] = useState(false)
  // BS-300 §1bis-G1 (đã CHỐT 2026-08-05) + BS-301 Route 2 §1 (2026-08-17):
  // backend never blocks an out-of-geofence clock-in — it accepts it and
  // returns `validation.is_within_geofence: false` +
  // `validation.distance_meters` (gps_attendance.py ClockInResponse). The web
  // page never read either field before this fix, so the "GPS lệch" warning
  // the backend already computes was silently dropped.
  const [gpsDeviationWarning, setGpsDeviationWarning] = useState<{ distanceMeters: number | null } | null>(null)

  // BS-300 §1bis-G2/G5 (PR #603 sync): non-blocking banner only — never disables clockIn.
  const timingWarning: ClockInTimingWarning = useMemo(
    () => computeClockInTimingWarning(todayShiftStartTime, earlyClockinToleranceMinutes),
    [todayShiftStartTime, earlyClockinToleranceMinutes],
  )

  // SC-017-EMP-ATTENDANCE [9] end_verify (2026-09-02) — non-blocking; caller
  // gates the "Lý do" textarea required/optional off `isLate`, never disables clockOut.
  const clockOutTimingWarning: ClockOutTimingWarning = useMemo(
    () => computeClockOutTimingWarning(todayShiftEndTime),
    [todayShiftEndTime],
  )

  // SC-017-EMP-ATTENDANCE [1] home IN-SHIFT (2026-09-04) — "{N} phút/giờ đã
  // trôi qua". Ticks every 60s while clocked in (mirrors the elapsed-time
  // pattern, not a live-second clock) so a long-open tab doesn't go stale;
  // no interval at all while not clocked in.
  const [elapsedTick, setElapsedTick] = useState(() => Date.now())
  useEffect(() => {
    if (serverStatus !== 'clocked_in') return
    const id = setInterval(() => setElapsedTick(Date.now()), 60000)
    return () => clearInterval(id)
  }, [serverStatus])
  const elapsedMinutes = useMemo(() => {
    if (serverStatus !== 'clocked_in' || !lastClockIn) return null
    return Math.max(0, Math.round((elapsedTick - new Date(lastClockIn).getTime()) / 60000))
  }, [serverStatus, lastClockIn, elapsedTick])

  // SC-017-EMP-ATTENDANCE [4] clock_in_confirm (2026-09-02) — PRE-submission
  // distance preview (✓/✗ Nm badge), mirroring mobile's `geofenceResult`
  // (AttendanceScreen.tsx GeoBadge). Client-side only, for immediate UI
  // feedback before the employee taps confirm — the backend's
  // `is_within_geofence`/`distance_meters` response (already read into
  // `gpsDeviationWarning` after clockIn) stays the actual source of truth;
  // this never blocks submission, same as the backend rule (BS-300 §1bis-G1).
  const geofenceResult = useMemo(() => {
    if (!location || !branchConfig?.coordinates) return null
    const distance = locationService.calculateDistance(
      location.lat,
      location.lon,
      branchConfig.coordinates.latitude,
      branchConfig.coordinates.longitude,
    )
    const threshold = branchConfig.max_clock_in_distance_meters
    return { distance, within: distance <= threshold }
  }, [location, branchConfig])

  // BS-300 §1bis (PR #603 sync, 2026-08-12): đóng dấu tên/GPS/địa chỉ/giờ lên
  // ảnh — route 2 trước đây KHÔNG có bước này (chỉ base64 nguyên ảnh). Đóng
  // dấu thất bại KHÔNG chặn chấm công (giống mobile fallbackToRawPhoto) —
  // dùng ảnh gốc. Dùng chung cho cả 2 nguồn ảnh: file input (selectPhoto) và
  // camera trong app (capturePhoto, SC-017-EMP-ATTENDANCE [3], 2026-09-02).
  const stampAndStorePhoto = useCallback(async (rawBase64: string) => {
    try {
      const stamped = await stampAttendancePhoto(rawBase64, {
        latitude: location?.lat ?? null,
        longitude: location?.lon ?? null,
        employeeName: user?.full_name || user?.email || '',
        accessToken: accessToken || '',
        // 2026-09-04 (mobile-source-of-truth audit, known bug #1) — same wrong
        // localStorage key as fetchBranchConfig above; read the real value off
        // the auth store instead. See that fix's comment for the full story.
        tenantKey: tenantKey || '',
      })
      setPhotoBase64(stamped)
    } catch {
      setPhotoBase64(rawBase64)
    }
  }, [location, user, accessToken, tenantKey])

  const selectPhoto = useCallback(async (file: File) => {
    const toBase64 = (f: File) => new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result))
      reader.onerror = reject
      reader.readAsDataURL(f)
    })
    const rawBase64 = await toBase64(file)
    await stampAndStorePhoto(rawBase64)
  }, [stampAndStorePhoto])

  // SC-017-EMP-ATTENDANCE [3] capture_photo (2026-09-02) — CameraCapture.tsx
  // hands back a canvas.toDataURL() data URL directly (no File object exists
  // for an in-app camera capture), so this skips selectPhoto's FileReader step.
  const capturePhoto = useCallback(async (photoDataUrl: string) => {
    await stampAndStorePhoto(photoDataUrl)
  }, [stampAndStorePhoto])

  const validateLocation = useCallback(async () => {
    setStatus('locating')
    setError(null)
    setMessage(null)
    setOverridden(false)
    try {
      const current = await locationService.location_current_get__uses_gps__returns_coords()
      setLocation(current)
      // Per gps-attendance-api.yaml §BranchLocation: use per-branch max_clock_in_distance_meters
      // Fallback chain: branch config → env var → default 100m (not 10m)
      const threshold = branchConfig?.max_clock_in_distance_meters
        ?? locationService.getAccuracyThreshold()
      const info = locationService.validateAccuracy(current.accuracy, threshold)
      setAccuracyInfo(info)
      if (!info.isValid) {
        setStatus('error')
        setError(`Độ chính xác GPS (${Math.round(info.accuracy)}m) vượt ngưỡng (${info.threshold}m).`)
        return null
      }
      setStatus('ready')
      return current
    } catch (e: any) {
      setStatus('error')
      setError('Không thể lấy vị trí hiện tại. Vui lòng bật định vị và thử lại.')
      return null
    }
  }, [branchConfig])

  const clockIn = useCallback(async () => {
    // Use returned location from validateLocation to avoid stale closure on first call
    let currentLocation = location
    if (!currentLocation) {
      currentLocation = await validateLocation()
      if (!currentLocation) return false
    }

    // If accuracy invalid and not overridden, block
    if (accuracyInfo && !accuracyInfo.isValid && !overridden) {
      setStatus('error')
      setError(`Độ chính xác GPS không đạt. Vui lòng cải thiện tín hiệu hoặc liên hệ quản trị để bỏ qua.`)
      return false
    }
    setStatus('submitting')
    setError(null)
    setMessage(null)
    setGpsDeviationWarning(null)

    const payload = {
      latitude: currentLocation.lat,
      longitude: currentLocation.lon,
      photo: photoBase64 || undefined,
      timestamp: new Date().toISOString(),
    }

    try {
      if (navigator.onLine) {
        const res = await apiWrapper.clockIn(payload.latitude, payload.longitude, payload.photo)
        if (res.success) {
          setStatus('success')
          setMessage('Chấm công thành công.')
          // 2026-08-29 (BOT-007 follow-up finding #2): clockOut() below always
          // sets serverStatus after a successful action (see 'clocked_out' at
          // every return path) but clockIn() never did the mirror-image
          // 'clocked_in' update — isClockedIn (AttendanceModern.tsx) reads
          // ONLY serverStatus, which was otherwise stuck at whatever the
          // once-on-mount fetch returned. Confirmed live: after a real
          // successful clock-in the card kept showing "Chấm công vào" (as if
          // never clocked in) until the page was manually reloaded, even
          // though the backend record was correct all along — reload re-ran
          // the mount fetch and picked up the right value. Setting it here
          // closes that gap at the source instead of relying on a reload.
          setServerStatus('clocked_in')
          // SC-017-EMP-ATTENDANCE [1] home IN-SHIFT (2026-09-04) — mirror of
          // the serverStatus fix above: elapsedMinutes reads lastClockIn, so
          // it must be set here too, not just on the once-on-mount fetch.
          setLastClockIn(payload.timestamp)
          // SC-017-EMP-ATTENDANCE [8] checklist — needs the real attendance_id
          // (AttendanceResponse.id); only available on the online-success path,
          // NOT the 3 offline-queued paths below (record doesn't exist server-side yet).
          const newAttendanceId = (res.data as any)?.id
          if (newAttendanceId) setCurrentAttendanceId(String(newAttendanceId))
          // BS-300 §1bis-G1 (CHỐT) + BS-301 Route 2 §1: backend accepts an
          // out-of-geofence clock-in and reports it via
          // validation.is_within_geofence=false — surface it as a
          // non-blocking warning, never as an error (the clock-in already
          // succeeded).
          const validation = (res.data as any)?.validation
          if (validation && validation.is_within_geofence === false) {
            setGpsDeviationWarning({ distanceMeters: typeof validation.distance_meters === 'number' ? validation.distance_meters : null })
          }
          return true
        }
        // #1317: any HTTP response (409 ambiguous branch, 422 shift limit,
        // 403, 4xx, 5xx) is the server's final answer — show it, never queue
        // it. Only a request that got no response at all queues offline.
        if (res.error !== 'Network error') {
          setStatus('error')
          setError(res.message || 'Không thể chấm công vào. Vui lòng thử lại.')
          return false
        }
        await offlineManager.queueAttendanceAction('clock_in', payload)
        setStatus('success')
        setMessage('Mạng không ổn định. Đã lưu chấm công offline và sẽ đồng bộ khi có mạng.')
        // Optimistic — same reasoning as the online-success path above, and
        // consistent with clockOut()'s offline paths (also set unconditionally).
        setServerStatus('clocked_in')
        setLastClockIn(payload.timestamp)
        return true
      } else {
        await offlineManager.queueAttendanceAction('clock_in', payload)
        setStatus('success')
        setMessage('Bạn đang offline. Đã lưu chấm công và sẽ đồng bộ khi có mạng.')
        setServerStatus('clocked_in')
        setLastClockIn(payload.timestamp)
        return true
      }
    } catch (e: any) {
      // Network/other error — queue offline
      await offlineManager.queueAttendanceAction('clock_in', payload)
      setStatus('success')
      setMessage('Không thể kết nối server. Đã lưu chấm công offline và sẽ đồng bộ khi có mạng.')
      setServerStatus('clocked_in')
      setLastClockIn(payload.timestamp)
      return true
    }
  }, [location, photoBase64, validateLocation, accuracyInfo, overridden])

  const clockOut = useCallback(async (notes?: string) => {
    // Use returned location from validateLocation to avoid stale closure on first call
    let currentLocation = location
    if (!currentLocation) {
      currentLocation = await validateLocation()
      if (!currentLocation) return false
    }

    setStatus('submitting')
    setError(null)
    setMessage(null)

    // BS-301 Route 2 §1 Responsive AC (2026-08-17): "Vào ca" and "Ra ca" must
    // BOTH stay usable offline and queue for later sync — clockOut previously
    // had no offline path at all (only clockIn queued), so a clock-out
    // attempted while offline just failed outright with no way to record it.
    const payload = { latitude: currentLocation.lat, longitude: currentLocation.lon, notes }
    if (!navigator.onLine) {
      await offlineManager.queueAttendanceAction('clock_out', payload)
      setStatus('success')
      setServerStatus('clocked_out')
      setLastClockIn(null)
      setMessage('Bạn đang offline. Đã lưu chấm công ra và sẽ đồng bộ khi có mạng.')
      return true
    }

    try {
      const res = await apiWrapper.clockOut(currentLocation.lat, currentLocation.lon, notes)
      if (res.success) {
        setStatus('success')
        setMessage('Chấm công ra thành công.')
        setServerStatus('clocked_out')
        setLastClockIn(null)
        return true
      }
      // apiWrapper's fetch-level catch also reports success:false with
      // error:'Network error' (apiWrapper.ts makeRequest, fetch rejection only) — that
      // case is connectivity, not a business rejection, so it queues offline
      // like clockIn does. A real server response (validation/500/etc) is a
      // genuine rejection and must surface as an error, not a false "saved".
      if (res.error === 'Network error') {
        await offlineManager.queueAttendanceAction('clock_out', payload)
        setStatus('success')
        setServerStatus('clocked_out')
        setLastClockIn(null)
        setMessage('Mạng không ổn định. Đã lưu chấm công ra offline và sẽ đồng bộ khi có mạng.')
        return true
      }
      setStatus('error')
      setError(res.message || 'Không thể chấm công ra. Vui lòng thử lại.')
      return false
    } catch {
      // Thrown exception (fetch itself failed) — treat as connectivity loss.
      await offlineManager.queueAttendanceAction('clock_out', payload)
      setStatus('success')
      setServerStatus('clocked_out')
      setLastClockIn(null)
      setMessage('Không thể kết nối server. Đã lưu chấm công ra offline và sẽ đồng bộ khi có mạng.')
      return true
    }
  }, [location, validateLocation])

  const reset = useCallback(() => {
    setStatus('idle')
    setError(null)
    setMessage(null)
    setPhotoBase64(null)
    setLocation(null)
    setAccuracyInfo(null)
    setOverridden(false)
    setGpsDeviationWarning(null)
  }, [])

  const overrideAccuracy = useCallback(() => {
    if (accuracyInfo && !accuracyInfo.isValid) {
      setOverridden(true)
      setAccuracyInfo({ ...accuracyInfo, isValid: true, message: 'Đã bỏ qua kiểm tra độ chính xác (quản trị)'} )
      setError(null)
      setStatus('ready')
    }
  }, [accuracyInfo])

  // 2026-08-29 (BOT-007 follow-up finding #1): the ORIGINAL Timemark
  // fallback (`useClockAction.ts` + `TimemarkForm.tsx`) was never ported
  // over when clock-in moved from EmployeeDashboard to this page+hook in
  // the 2026-08-25 redesign (see AttendanceModern.tsx header note "gỡ hoàn
  // toàn flow chấm công tại chỗ... khỏi HUB") — verified live: with GPS
  // permission denied, this page dead-ends on "Không thể lấy vị trí hiện
  // tại..." with NO way to proceed at all, worse than the original bug
  // #670 (which was at least reachable, just 404'd). `TimemarkForm.tsx` is
  // still a fully self-contained component (own submit call to
  // `attendanceApi.submitTimemark`, no dependency on the old hook) — only
  // a trigger point was missing here. Restoring it as an explicit
  // human-initiated fallback (never auto-opened) offered once GPS has
  // failed, mirroring the old UX ("GPS fail → offer Timemark").
  const [showTimemark, setShowTimemark] = useState(false)
  const triggerTimemark = useCallback(() => setShowTimemark(true), [])
  const closeTimemark = useCallback(() => setShowTimemark(false), [])

  return useMemo(() => ({
    // state
    status,
    serverStatus,
    staleCloseMode,
    currentAttendanceId,
    // SC-017-EMP-ATTENDANCE [1] home IN-SHIFT (2026-09-04)
    lastClockIn,
    elapsedMinutes,
    error,
    message,
    photoBase64,
    location,
    accuracyInfo,
    overridden,
    // BS-300 §1bis-G2/G5 (PR #603 sync) — non-blocking banner state
    timingWarning,
    clockOutTimingWarning,
    geofenceResult,
    branchConfig,
    earlyClockinToleranceMinutes,
    // web-sync checklist A.5 (2026-08-15) — branch/location fetch error + retry
    branchConfigError,
    // SC-017-EMP-ATTENDANCE [2] (2026-09-01) — config-driven method visibility
    methodConfig,
    // BS-301 Route 2 §1 (2026-08-17) — "no shift today" empty-state
    todayScheduleChecked,
    hasShiftToday,
    // BS-301 Route 2 §1 (2026-08-17) — GPS-lệch (out-of-geofence) banner
    gpsDeviationWarning,
    // BOT-007 follow-up (2026-08-29) — Timemark fallback, offered when GPS fails
    showTimemark,
    // actions
    selectPhoto,
    capturePhoto,
    validateLocation,
    clockIn,
    clockOut,
    reset,
    overrideAccuracy,
    retryBranchConfig: fetchBranchConfig,
    triggerTimemark,
    closeTimemark,
  }), [status, serverStatus, staleCloseMode, currentAttendanceId, lastClockIn, elapsedMinutes, error, message, photoBase64, location, accuracyInfo, overridden, timingWarning, clockOutTimingWarning, geofenceResult, branchConfig, earlyClockinToleranceMinutes, branchConfigError, methodConfig, todayScheduleChecked, hasShiftToday, gpsDeviationWarning, showTimemark, selectPhoto, capturePhoto, validateLocation, clockIn, clockOut, reset, overrideAccuracy, fetchBranchConfig, triggerTimemark, closeTimemark])
}
